'use client';

import { useState } from 'react';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '../DropdownMenu';
import { PERMISSIONS, AuthenticatedUser, FeatureFlagsSettings } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../helpers';

const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';
interface Props {
	authenticatedUser: AuthenticatedUser | null;
	currentPath: 'admin' | 'company' | 'personal' | 'supervisor' | 'partner';
	featureFlags?: FeatureFlagsSettings['settings'];
}

const viteBasePath = isLocalhost ? 'http://localhost:5174' : '';
const nextBasePath = isLocalhost ? 'http://localhost:3000' : '';

const menuItems = [
	{
		label: 'Admin',
		id: 'admin',
		permission: PERMISSIONS.adminView,
		path: `${viteBasePath}/`,
	},
	{
		label: 'Company',
		id: 'company',
		permission: PERMISSIONS.companyView,
		path: `${viteBasePath}/company`,
	},
	{
		label: 'Personal',
		id: 'personal',
		permission: PERMISSIONS.personalView,
		path: `${viteBasePath}/personal`,
	},
	{
		label: 'Supervisor',
		id: 'supervisor',
		permission: PERMISSIONS.supervisorView,
		path: `${viteBasePath}/supervisor`,
	},
	{
		label: 'Partner',
		id: 'partner',
		permission: PERMISSIONS.partnerCompanyView,
		path: `${viteBasePath}/partner`,
	},
] as const;

export default function ViewSwitcher({ authenticatedUser, currentPath, featureFlags = {} }: Props) {
	const [open, setOpen] = useState(false);
	if (!authenticatedUser) {
		return null;
	}
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions);

	const switchView = (path: (typeof menuItems)[number]['path']) => {
		window.location.href = path;
	};

	const currentMenuItem = menuItems.find((item) => item.id === currentPath);

	const menuItemsFiltered = menuItems.filter((item) => {
		if (item.id === 'supervisor' && !featureFlags.USES_SUPERVISOR) {
			return false;
		}
		if (item.id === 'partner' && !featureFlags.USES_PARTNER_PORTAL) {
			return false;
		}
		return verifyPermission(item.permission) && item.id !== currentPath;
	});
	if (menuItemsFiltered.length === 0) {
		return null;
	}

	return (
		<>
			<DropdownMenu open={open} onOpenChange={setOpen}>
				<DropdownMenuTrigger asChild>
					<button className="h-[var(--top-menu-height)]">
						<div className="h-full flex items-center hover:bg-white/10">
							<div className="flex items-center gap-2 px-4 text-white">
								<span className="truncate text-sm">{currentMenuItem?.label}</span>
								<ChevronDown
									className={cn('h-4 w-4 shrink-0 transition-transform duration-200', open && 'rotate-180')}
								/>
							</div>
						</div>
					</button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="start" className="min-w-[200px]">
					<DropdownMenuLabel className="font-semibold">{currentMenuItem?.label}</DropdownMenuLabel>
					<DropdownMenuSeparator />
					{menuItemsFiltered.map((item) => (
						<DropdownMenuItem
							key={item.path}
							onClick={() => {
								switchView(item.path);
							}}
						>
							{item.label}
						</DropdownMenuItem>
					))}
				</DropdownMenuContent>
			</DropdownMenu>
		</>
	);
}
